import Phaser from 'phaser';

/**
 * Lumière d'ambiance — couche de lumière du « Monde du campus » de Charles
 * (design system, composant Lumière ; réglages = jetons de la famille
 * lumiere + assets/Cartes/lumiere.json). Posée par-dessus les pixels, jamais
 * dans les images.
 *
 * Le prototype de Charles dessine deux canvas HTML posés sur le jeu, fusionnés
 * par le navigateur (mix-blend-mode soft-light / multiply). Ici tout tient en
 * UNE passe plein écran dans le moteur : un PostFX de caméra dont le shader
 * applique, dans l'ordre, la saturation (×1,12), le calque doux (dégradé chaud
 * → frais + soleil, soft-light) puis le calque sombre (ombres de nuages +
 * vignette, multiply) — mêmes formules que les modes de fusion CSS (W3C
 * Compositing), mêmes couleurs, forces et positions de nuages.
 *
 * Profondeur : la caméra principale (avec le PostFX) ne rend que ce qui est
 * sous LIGHT_DEPTH (décor, avatars, murs) ; une seconde caméra, calée chaque
 * image sur la principale, rend sans effet les repères au-dessus (voile de
 * zone, labels de bureau, ping, réactions) — même place que le voile de
 * ZoneDimmer dans l'ordre des profondeurs. Le mobilier « au-dessus du
 * mobilier » (999) reste du décor, donc éclairé.
 *
 * WebGL uniquement : en rendu Canvas, aucun effet (carte normale).
 */

// ── Réglages (copiés des jetons de Charles) ────────────────────────────────
const L = {
  chaud: [255, 214, 150],
  frais: [118, 132, 140],
  forceDegrade: 0.45,
  ombreNuage: [66, 138, 160],
  forceNuage: 0.62,
  soleil: [255, 206, 130],
  forceSoleil: 0.35,
  vignette: [52, 112, 132],
  forceVignette: 0.3,
  /** Dedans, la vignette est plus forte. */
  vignetteDedans: 1.4,
  saturation: 1.12,
  /** [x, y, rayon x, rayon y], en fraction de la largeur/hauteur de la carte. */
  nuages: [
    [0.8, 0.16, 0.16, 0.1],
    [0.92, 0.24, 0.12, 0.09],
    [0.7, 0.22, 0.1, 0.07],
    [0.12, 0.74, 0.16, 0.1],
    [0.26, 0.82, 0.14, 0.09],
    [0.08, 0.88, 0.1, 0.08],
    [0.62, 0.96, 0.14, 0.06],
  ],
  /** Dérive des nuages, px monde par seconde (vertical : × 0,4). */
  deriveNuage: 6,
} as const;

/** Tout ce qui est à cette profondeur ou plus est rendu sans lumière… */
export const LIGHT_DEPTH = 840;
/** …jusqu'ici (exclu) : 999 = mobilier « au-dessus du mobilier », du décor. */
const UNLIT_MAX_DEPTH = 990;

const PIPELINE_KEY = 'PlacioAmbientLight';
const CLOUD_COUNT = L.nuages.length;

const rgb = (c: readonly number[]) => `vec3(${(c[0]! / 255).toFixed(4)}, ${(c[1]! / 255).toFixed(4)}, ${(c[2]! / 255).toFixed(4)})`;

const FRAG = `
#define SHADER_NAME PLACIO_AMBIENT_LIGHT_FS
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform float uOutdoor;
uniform vec4 uClouds[${CLOUD_COUNT}];
varying vec2 outTexCoord;

const vec3 CHAUD = ${rgb(L.chaud)};
const vec3 FRAIS = ${rgb(L.frais)};
const vec3 SOLEIL = ${rgb(L.soleil)};
const vec3 NUAGE = ${rgb(L.ombreNuage)};
const vec3 VIGNETTE = ${rgb(L.vignette)};

// mix-blend-mode: soft-light (W3C Compositing and Blending, par canal)
vec3 softLight(vec3 cb, vec3 cs) {
  vec3 d = mix(sqrt(cb), ((16.0 * cb - 12.0) * cb + 4.0) * cb, step(cb, vec3(0.25)));
  vec3 dark = cb - (1.0 - 2.0 * cs) * cb * (1.0 - cb);
  vec3 light = cb + (2.0 * cs - 1.0) * (d - cb);
  return mix(light, dark, step(cs, vec3(0.5)));
}

void main() {
  vec4 tex = texture2D(uMainSampler, outTexCoord);
  if (tex.a <= 0.0) { gl_FragColor = tex; return; }
  vec3 c = tex.rgb / tex.a;

  // Saturation (même matrice que ColorMatrix.saturate(${(L.saturation - 1).toFixed(2)}) de Phaser)
  float sx = 1.0 + ${(L.saturation - 1).toFixed(4)} * 2.0 / 3.0;
  float sy = (sx - 1.0) * -0.5;
  c = clamp(vec3(
    sx * c.r + sy * c.g + sy * c.b,
    sy * c.r + sx * c.g + sy * c.b,
    sy * c.r + sy * c.g + sx * c.b), 0.0, 1.0);

  // Pixel écran, origine en haut à gauche (la texture de la caméra a y vers le haut).
  vec2 p = vec2(outTexCoord.x, 1.0 - outTexCoord.y) * uResolution;

  // ── Calque doux (soft-light) : dégradé chaud (haut gauche) → frais (bas droite)
  float t = clamp(dot(p, uResolution) / dot(uResolution, uResolution), 0.0, 1.0);
  vec3 g = mix(CHAUD, FRAIS, t);
  float ag = ${L.forceDegrade.toFixed(4)};
  vec3 co = ag * g;
  float ao = ag;
  if (uOutdoor > 0.5) {
    // Soleil posé en soft-light sur le dégradé, à l'intérieur du calque.
    float as = ${L.forceSoleil.toFixed(4)};
    vec3 cs = (1.0 - ag) * SOLEIL + ag * softLight(g, SOLEIL);
    co = as * cs + ag * g * (1.0 - as);
    ao = as + ag * (1.0 - as);
  }
  c = (1.0 - ao) * c + ao * softLight(c, co / ao);

  // ── Calque sombre (multiply) : ombres de nuages puis vignette
  float an = 0.0;
  if (uOutdoor > 0.5) {
    float keep = 1.0;
    for (int i = 0; i < ${CLOUD_COUNT}; i++) {
      vec4 cl = uClouds[i];
      float d = length((p - cl.xy) / cl.zw);
      keep *= 1.0 - ${L.forceNuage.toFixed(4)} * clamp(1.0 - d, 0.0, 1.0);
    }
    an = 1.0 - keep;
  }
  vec2 center = uResolution * 0.5;
  float r0 = min(uResolution.x, uResolution.y) * 0.35;
  float r1 = max(uResolution.x, uResolution.y) * 0.75;
  float vForce = ${L.forceVignette.toFixed(4)} * (uOutdoor > 0.5 ? 1.0 : ${L.vignetteDedans.toFixed(4)});
  float av = vForce * clamp((length(p - center) - r0) / (r1 - r0), 0.0, 1.0);
  vec3 vs = (1.0 - an) * VIGNETTE + an * NUAGE * VIGNETTE;
  vec3 dark = av * vs + an * NUAGE * (1.0 - av);
  float adark = av + an * (1.0 - av);
  c *= (1.0 - adark) + dark;

  gl_FragColor = vec4(c * tex.a, tex.a);
}
`;

/** Paramètres de l'image en cours, écrits par AmbientLight, lus par le pipeline. */
interface LightFrame {
  width: number;
  height: number;
  outdoor: boolean;
  clouds: Float32Array;
  /** Appelé au moment du rendu de la caméra principale (vue de l'image en cours) ; faux si la scène n'a plus de caméra. */
  compute: () => boolean;
}

class AmbientLightPipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  frame: LightFrame | null = null;

  constructor(game: Phaser.Game) {
    super({ game, name: PIPELINE_KEY, fragShader: FRAG });
  }

  override onPreRender(): void {
    const f = this.frame;
    // Lumière éteinte ou scène arrêtée (changement de carte) : rien à calculer.
    // Une exception ici casse la boucle du moteur à chaque image (carte figée sur « Chargement »).
    if (!f || !f.compute()) return;
    this.set2f('uResolution', f.width, f.height);
    this.set1f('uOutdoor', f.outdoor ? 1 : 0);
    this.set4fv('uClouds', f.clouds);
  }
}

const reducedMotionQuery = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null;

export interface AmbientLightOptions {
  /** Largeur/hauteur de la carte en px monde (place et dérive des nuages). */
  mapWidth: number;
  mapHeight: number;
  /** Réglage « Réduire les animations » de Placio (en plus de celui du système). */
  reduceMotion: () => boolean;
}

/**
 * Allume/éteint la lumière d'ambiance d'une scène. Rien n'est alloué tant
 * qu'elle est éteinte (pas de pipeline, pas de seconde caméra).
 */
export class AmbientLight {
  private readonly scene: Phaser.Scene;
  private readonly opts: AmbientLightOptions;
  private readonly frame: LightFrame;
  private overlayCam: Phaser.Cameras.Scene2D.Camera | null = null;
  /** Caméra et instance d'effet de l'allumage, gardées pour les détacher même scène déjà démontée. */
  private mainCam: Phaser.Cameras.Scene2D.Camera | null = null;
  private pipe: AmbientLightPipeline | null = null;
  private enabled = false;
  /** Coût CPU moyen de la préparation par image (ms), pour la mesure. */
  private cpuMs = 0;

  /** Dehors (nuages + soleil) ou dedans (dégradé + vignette ×1,4). */
  outdoor = true;

  constructor(scene: Phaser.Scene, opts: AmbientLightOptions) {
    this.scene = scene;
    this.opts = opts;
    this.frame = {
      width: 1, height: 1, outdoor: true,
      clouds: new Float32Array(CLOUD_COUNT * 4),
      compute: () => this.computeUniforms(),
    };
  }

  static supported(scene: Phaser.Scene): boolean {
    return scene.sys.renderer.type === Phaser.WEBGL;
  }

  isEnabled(): boolean { return this.enabled; }

  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    if (on && !AmbientLight.supported(this.scene)) return;
    this.enabled = on;
    if (on) this.attach();
    else this.detach();
  }

  destroy(): void {
    this.setEnabled(false);
  }

  /** Mesure exposée pour la console (voir CLAUDE.md, « Lumière d'ambiance »). */
  stats(): { cpuMsPerFrame: number } {
    return { cpuMsPerFrame: Math.round(this.cpuMs * 1000) / 1000 };
  }

  private attach(): void {
    const scene = this.scene;
    const renderer = scene.sys.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
    if (!renderer.pipelines.postPipelineClasses.has(PIPELINE_KEY)) {
      renderer.pipelines.addPostPipeline(PIPELINE_KEY, AmbientLightPipeline);
    }
    const main = scene.cameras.main;
    main.setPostPipeline(PIPELINE_KEY);
    const pipe = main.getPostPipeline(PIPELINE_KEY);
    const inst = (Array.isArray(pipe) ? pipe[0] : pipe) as AmbientLightPipeline | undefined;
    if (inst) inst.frame = this.frame;
    this.mainCam = main;
    this.pipe = inst ?? null;

    // Seconde caméra : rend, sans effet, les repères au-dessus de la lumière.
    // Recopie la principale dans SON preRender, qui suit celui de la principale
    // (ordre des caméras) : scroll final de l'image, suivi et bornes compris —
    // une recopie dans PRE_RENDER aurait une image de retard (labels qui
    // tremblent quand la caméra suit l'avatar).
    const overlay = scene.cameras.add(main.x, main.y, main.width, main.height, false, 'ambient-light-overlay');
    const basePreRender = overlay.preRender.bind(overlay);
    overlay.preRender = () => {
      if (overlay.width !== main.width || overlay.height !== main.height) overlay.setSize(main.width, main.height);
      overlay.setZoom(main.zoomX, main.zoomY);
      overlay.setRoundPixels(main.roundPixels);
      overlay.scrollX = main.scrollX;
      overlay.scrollY = main.scrollY;
      basePreRender();
    };
    this.overlayCam = overlay;

    scene.events.on(Phaser.Scenes.Events.PRE_RENDER, this.prepareFrame, this);
  }

  private detach(): void {
    const scene = this.scene;
    scene.events.off(Phaser.Scenes.Events.PRE_RENDER, this.prepareFrame, this);
    // Pendant l'arrêt de la scène, caméras et display list peuvent être déjà démontées :
    // on coupe l'effet par les références gardées à l'allumage, jamais par scene.cameras.main.
    if (this.pipe) this.pipe.frame = null;
    this.pipe = null;
    this.mainCam?.removePostPipeline(PIPELINE_KEY);
    this.mainCam = null;
    const overlay = this.overlayCam;
    if (overlay && scene.cameras?.cameras.includes(overlay)) scene.cameras.remove(overlay);
    for (const child of scene.children?.list ?? []) child.cameraFilter = 0;
    this.overlayCam = null;
  }

  /** Chaque image, avant le rendu : répartition entre caméras + uniformes du shader. */
  private prepareFrame(): void {
    const overlay = this.overlayCam;
    if (!overlay) return;
    const t0 = performance.now();
    const scene = this.scene;
    const main = scene.cameras.main;

    // Un objet n'est rendu que par une des deux caméras (bit de cameraFilter = ignoré).
    const mainId = main.id;
    const overlayId = overlay.id;
    for (const child of scene.children.list) {
      const depth = (child as Partial<Phaser.GameObjects.Components.Depth>).depth ?? 0;
      const unlit = depth >= LIGHT_DEPTH && depth < UNLIT_MAX_DEPTH;
      child.cameraFilter = unlit ? mainId : overlayId;
    }

    this.cpuMs = this.cpuMs * 0.95 + (performance.now() - t0) * 0.05;
  }

  /** Uniformes du shader, calculés pendant le rendu de la caméra principale (worldView à jour). */
  private computeUniforms(): boolean {
    const scene = this.scene;
    const main = scene.cameras?.main;
    if (!main || !this.enabled) return false;
    const f = this.frame;
    f.width = main.width;
    f.height = main.height;
    f.outdoor = this.outdoor;

    // Nuages : placés en coordonnées monde, convertis en pixels écran.
    const W = this.opts.mapWidth;
    const H = this.opts.mapHeight;
    const still = reducedMotionQuery?.matches || this.opts.reduceMotion();
    const drift = still ? 0 : (scene.time.now / 1000) * L.deriveNuage;
    const zoom = main.zoom;
    const view = main.worldView;
    L.nuages.forEach(([fx, fy, rx, ry], i) => {
      const x = ((fx * W + drift) % (W + 300)) - 150;
      const y = ((fy * H + drift * 0.4) % (H + 200)) - 100;
      f.clouds[i * 4] = (x - view.x) * zoom;
      f.clouds[i * 4 + 1] = (y - view.y) * zoom;
      f.clouds[i * 4 + 2] = Math.max(rx * W, ry * H) * zoom;
      f.clouds[i * 4 + 3] = Math.max(ry * H * zoom, 0.001);
    });
    return true;
  }
}
